import { gql, useQuery } from '@apollo/client';
import { toPagination } from './utils';

export const GET_SUBMISSIONS_BY_ENTITY = gql`
  query GetSubmissionsByEntity(
    $insuredEntityUuid: String!
    $pagination: PaginationRequest
  ) {
    submissions(
      pagination: $pagination
      filters: { nestedFilters: { entityUuid: $insuredEntityUuid } }
    ) {
      pageInfo {
        totalResults
        pageNumber
        pageSize
        hasNextPage
        hasPreviousPage
      }
      edges {
        node {
          uuid
          referenceId
          type
          renewal
          renewalType
          status
          createdAt
          product {
            node {
              uuid
              name
            }
          }
          brokerEntity {
            node {
              uuid
              aliases(filters: { groupSources: true }) {
                edges {
                  node {
                    uuid
                    name
                    type
                    primary
                    sources
                  }
                }
              }
            }
          }
          persons {
            edges {
              node {
                uuid
                type
                firstName
                lastName
              }
            }
          }
          policyStartDate
        }
      }
    }
  }
`;

export interface InsuredEntitySubmissionNode {
  uuid: string;
  referenceId?: string | null;
  type?: string | null;
  renewal?: boolean | null;
  renewalType?: string | null;
  status?: string | null;
  createdAt?: string | null;
  product?: {
    node?: {
      uuid?: string;
      name?: string | null;
    } | null;
  } | null;
  brokerEntity?: {
    node?: {
      uuid?: string;
      aliases?: {
        edges?: Array<{
          node?: {
            uuid?: string;
            name?: string | null;
            type?: string | null;
            primary?: boolean | null;
            sources?: string[] | null;
          } | null;
        } | null> | null;
      } | null;
    } | null;
  } | null;
  persons?: {
    edges?: Array<{
      node?: {
        uuid?: string;
        type?: string | null;
        firstName?: string | null;
        lastName?: string | null;
      } | null;
    } | null> | null;
  } | null;
  policyStartDate?: string | null;
}

export interface GetSubmissionsByInsuredEntityData {
  submissions: {
    pageInfo: {
      totalResults: number;
      pageNumber: number;
      pageSize: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
    edges: Array<{
      node: InsuredEntitySubmissionNode;
    }>;
  };
}

export function useInsuredEntitySubmissionsQuery({
  insuredEntityUuid,
  pageNumber,
  pageSize,
}: {
  insuredEntityUuid: string;
  pageNumber: number;
  pageSize: number;
}) {
  const { loading, error, data } = useQuery<GetSubmissionsByInsuredEntityData>(
    GET_SUBMISSIONS_BY_ENTITY,
    {
      variables: {
        insuredEntityUuid,
        pagination: toPagination(pageNumber, pageSize),
      },
      skip: !insuredEntityUuid,
      fetchPolicy: 'cache-and-network',
    }
  );

  return { loading, error, data };
}
